/**
 * fuarAlaniSalonService.js — Fuar Alanları ve salonları (backlog #24 / #26, 2026-09-15)
 *
 * Base URL: {JAVA_BACKEND_API_URL}/fuar/alani
 * Auth: getAuthHeader() — Basic Auth + X-user-Id + X-token (FuarController'da bypass yok → 401)
 * Kaynak: api-endpoints.md (FDS-Backend) — FuarController "FAIRGROUNDS AND THEIR HALLS"
 *
 * Bir salon bir fuar alanına `fuar_alani_salon` ile kaydedilir; salonun FİZİKSEL TAVANI
 * (`ceilingHeight`) ve VARSAYILAN İZİN VERİLEN YÜKSEKLİĞİ (`allowedHMax`) orada, fuar alanı
 * seviyesinde tutulur. Fuar Kuralları → Yükseklik Limitleri sekmesi ikisini SALT OKUNUR gösterir.
 *
 * ⚠️ Eski `fuarAlaniService.js` + `fuarAlaniSlice.js` HİÇ var olmayan `/api/v1/fuaralani/**`
 *    ad alanına gidiyordu (hep mock). Bu dosya onun yerine gerçek uçlara gider; eski dosyalara
 *    dokunulmadı.
 *
 * ⚠️ Hata sözleşmesi: 409 = başka kayıtlar engelliyor (salonu kullanan fuarlar, alınmış ad,
 *    başka fuar alanıyla paylaşılan salon kaydı) ve gövdenin `data`sı engelleri LİSTELER →
 *    `err.conflicts`. 400 = istek hatalı (ör. izin verilen yükseklik tavandan büyük).
 * ⚠️ Salon adı değişince mevcut stand adları ESKİ salon adını taşımaya devam eder
 *    (stand_name "{salon}.{kod}") — backend yeniden yazmaz.
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

const BASE = `${JAVA_BACKEND_API_URL}/fuar/alani`;
const FUAR_BASE = `${JAVA_BACKEND_API_URL}/fuar`;

function toError(error, fallback = i18n.t("services:fuarAlaniSalonService.islemTamamlanamadi")) {
  const status = error?.response?.status ?? null;
  const data = error?.response?.data;
  let message = fallback;
  if (status === 401) message = i18n.t("services:fuarAlaniSalonService.oturumGecersiz401LutfenYeniden");
  else if (typeof data === 'string' && data.trim()) message = data;
  else if (data && typeof data === 'object' && data.message) message = data.message;
  else if (error?.message) message = error.message;
  const err = new Error(message);
  err.status = status;
  if (Array.isArray(data?.data)) err.conflicts = data.data;
  return err;
}

/** Yazma yanıtlarındaki {message, result, data} zarfı; result:false da hata sayılır. */
function unwrap(payload) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload) && 'result' in payload) {
    if (payload.result === false) {
      const err = new Error(payload.message || i18n.t("services:fuarAlaniSalonService.islemTamamlanamadi"));
      if (Array.isArray(payload.data)) err.conflicts = payload.data;
      throw err;
    }
    return { message: payload.message, data: payload.data ?? null };
  }
  return { message: null, data: payload };
}

const num = (v) => (v === '' || v == null ? null : Number(v));

/** Tüm fuar alanları: [{ fuarAlaniId, fuarAlaniName, salonCount, fuarCount }] — GET /fuar/alani/list */
export async function listFuarAlanlari() {
  try {
    const res = await axios.get(`${BASE}/list`, { headers: getAuthHeader() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { throw toError(e, i18n.t("services:fuarAlaniSalonService.fuarAlanlariYuklenemedi")); }
}

/** Bir fuar alanı + salonları: { fuarAlaniId, fuarAlaniName, halls: [...] } — 404 yoksa. */
export async function getFuarAlani(fuarAlaniId) {
  try {
    const res = await axios.get(`${BASE}/${fuarAlaniId}`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { throw toError(e, i18n.t("services:fuarAlaniSalonService.fuarAlaniYuklenemedi")); }
}

/** Yeni fuar alanı. Dönüş: oluşturulan fuar alanı (id dahil). — POST /fuar/alani */
export async function createFuarAlani(fuarAlaniName) {
  try {
    const res = await axios.post(BASE, { fuarAlaniName }, { headers: getAuthHeader() });
    return unwrap(res.data).data;
  } catch (e) { throw e.response ? toError(e, i18n.t("services:fuarAlaniSalonService.fuarAlaniOlusturulamadi")) : e; }
}

/** Fuar alanının adını değiştirir. — PUT /fuar/alani/{id} */
export async function renameFuarAlani(fuarAlaniId, fuarAlaniName) {
  try {
    const res = await axios.put(`${BASE}/${fuarAlaniId}`, { fuarAlaniName }, { headers: getAuthHeader() });
    return unwrap(res.data).data;
  } catch (e) { throw e.response ? toError(e, i18n.t("services:fuarAlaniSalonService.fuarAlaniKaydedilemedi")) : e; }
}

/**
 * Fuar alanının salonları:
 * [{ fuarAlaniSalonId, salonId, salonName, brutM2, ceilingHeight, allowedHMax, headroom,
 *    sharedWithOtherFairgrounds, fuarCount, hizmetler: [{ kod, ad, durum, durumKod, durumLabel }],
 *    salonPlaniDurum, salonPlaniDurumTarihi, altyapiPlaniDurum, altyapiPlaniDurumTarihi }]
 * GET /fuar/alani/{id}/salonlar
 */
export async function listSalonlar(fuarAlaniId) {
  try {
    const res = await axios.get(`${BASE}/${fuarAlaniId}/salonlar`, { headers: getAuthHeader() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { throw toError(e, i18n.t("services:fuarAlaniSalonService.salonlarYuklenemedi")); }
}

/**
 * Salon ekler: { salonName } yeni salon, ya da { salonId } mevcut bir salon kaydını bu fuar
 * alanına da bağlar; isteğe bağlı ceilingHeight, allowedHMax (metre). Daha önce kaldırılmış
 * salon yeniden etkinleşir. 409 → zaten kayıtlı / ad alınmış.
 * POST /fuar/alani/{id}/salonlar
 */
export async function addSalon(fuarAlaniId, { salonName, salonId, ceilingHeight, allowedHMax } = {}) {
  const body = { ceilingHeight: num(ceilingHeight), allowedHMax: num(allowedHMax) };
  if (salonId != null) body.salonId = salonId; else body.salonName = (salonName || '').trim();
  try {
    const res = await axios.post(`${BASE}/${fuarAlaniId}/salonlar`, body, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { throw e.response ? toError(e, 'Salon eklenemedi.') : e; }
}

/**
 * Salonu düzenler — yalnızca `patch` içindeki anahtarlar değişir: salonName, ceilingHeight,
 * allowedHMax (boş/null temizler), brutM2 (boş/null temizler), hizmetler.
 *
 * hizmetler (backlog #194, 2026-09-26): {kod: durum} — 0 yok / 1 var / 2 harici destek, null =
 * BİLİNMİYOR'a geri döndür (kayıt silinir). WAS {kod: true/false} (#100/#101; backend hâlâ kabul
 * ediyor). brutM2 ve hizmetler V53'ten beri FUAR ALANINDAKİ salona aittir — WAS salon kaydının
 * kendisine: bir salon kaydı birden çok fuar alanında paylaşıldığı için (ör. "1" altı fuar
 * alanında) bir fuar alanında girilen hizmet ötekilere de sızıyordu. 400 → izin verilen > tavan,
 * brutM2 negatif ya da okunamayan hizmet durumu (hiçbir şey yazılmadan). 409 → bir fuarın bu salondaki tek/çift katlı limiti yeni değerin üstünde
 * (geçmiş fuarlar dahil), ad alınmış ya da salon kaydı başka bir fuar alanıyla paylaşılıyor
 * (ad değiştirilemez).
 * PUT /fuar/alani/{id}/salonlar/{salonId}
 */
export async function updateSalon(fuarAlaniId, salonId, patch = {}) {
  const body = {};
  if ('salonName' in patch) body.salonName = (patch.salonName || '').trim();
  if ('ceilingHeight' in patch) body.ceilingHeight = num(patch.ceilingHeight);
  if ('allowedHMax' in patch) body.allowedHMax = num(patch.allowedHMax);
  if ('brutM2' in patch) body.brutM2 = num(patch.brutM2);
  if ('hizmetler' in patch) body.hizmetler = patch.hizmetler;
  try {
    const res = await axios.put(`${BASE}/${fuarAlaniId}/salonlar/${salonId}`, body, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { throw e.response ? toError(e, 'Salon kaydedilemedi.') : e; }
}

/**
 * Salon hizmet/altyapı kataloğu (Elektrik, Su, Hava, İnternet — admin tarafından genişletilebilir
 * ama bugün yalnızca okunuyor): [{ salonHizmetTipiId, kod, ad, sira }]
 * GET /fuar/salon-hizmet-tipleri
 */
export async function listSalonHizmetTipleri() {
  try {
    const res = await axios.get(`${FUAR_BASE}/salon-hizmet-tipleri`, { headers: getAuthHeader() });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { throw toError(e, i18n.t("services:fuarAlaniSalonService.salonHizmetTipleriYuklenemedi")); }
}

// ── Çizim durumları (backlog #202, V53) ─────────────────────────────────────────────────
// 0 çizim yok / 1 çiziliyor / 2 tamamlandı (CIZIM_DURUM, Components/FairAreas/salonDurumlari.js).
// Salon Planı + Altyapı Planı FUAR ALANINDAKİ salona, Alan Planı FUARDAKİ salona (salon_to_fuar)
// aittir. Her yazım ne zaman / kim bilgisini damgalar — Alan Planı = 2 "Stand Çizimi Tamamlandı",
// tarihi tamamlanma zamanıdır. Yalnızca personel (firma oturumu 403). Dönüş: güncel salon satırı.

async function putCizimDurumu(url, durum, fallback) {
  try {
    const res = await axios.put(url, { durum }, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { throw e.response ? toError(e, fallback) : e; }
}

/** PUT /fuar/alani/{fuarAlaniId}/salonlar/{salonId}/salon-plani-durumu — 404: salon bu fuar alanında kayıtlı değil. */
export function setSalonPlaniDurumu(fuarAlaniId, salonId, durum) {
  return putCizimDurumu(`${BASE}/${fuarAlaniId}/salonlar/${salonId}/salon-plani-durumu`, durum,
    i18n.t("services:fuarAlaniSalonService.salonPlaniDurumuKaydedilemedi"));
}

/** PUT /fuar/alani/{fuarAlaniId}/salonlar/{salonId}/altyapi-plani-durumu */
export function setAltyapiPlaniDurumu(fuarAlaniId, salonId, durum) {
  return putCizimDurumu(`${BASE}/${fuarAlaniId}/salonlar/${salonId}/altyapi-plani-durumu`, durum,
    i18n.t("services:fuarAlaniSalonService.altyapiPlaniDurumuKaydedilemedi"));
}

/** PUT /fuar/{fuarId}/salon-to-fuar/{salonId}/alan-plani-durumu — 404: salon bu fuara bağlı değil. Dönüş data: getFuarHMax'in o salon satırı. */
export function setAlanPlaniDurumu(fuarId, salonId, durum) {
  return putCizimDurumu(`${FUAR_BASE}/${fuarId}/salon-to-fuar/${salonId}/alan-plani-durumu`, durum,
    i18n.t("services:fuarAlaniSalonService.alanPlaniDurumuKaydedilemedi"));
}

/** Salonu fuar alanından kaldırır (yumuşak silme). 409 → bu fuar alanında onu kullanan canlı fuarlar var. */
export async function removeSalon(fuarAlaniId, salonId) {
  try {
    const res = await axios.delete(`${BASE}/${fuarAlaniId}/salonlar/${salonId}`, { headers: getAuthHeader() });
    return unwrap(res.data);
  } catch (e) { throw e.response ? toError(e, i18n.t("services:fuarAlaniSalonService.salonKaldirilamadi")) : e; }
}

/**
 * Backlog #157: fuar alanına kayıtlı bir salonu BU FUARA bağlar — bağlanmadan bir salon
 * FuarKurallariPage'in Yükseklik Limitleri sekmesinde hiç görünmez (GET /fuar/{fuarId}/h-max
 * yalnızca zaten salon_to_fuar satırı olan salonları döner). Bu uç, o satırı YARATAN tek yer —
 * 2026-09-23'e kadar backend'de hiçbir yerde INSERT INTO salon_to_fuar yoktu.
 * 409 → salon bu fuara zaten bağlı. 400 → salon bu fuarın fuar alanında kayıtlı değil.
 * POST /fuar/{fuarId}/salon-to-fuar/{salonId}
 */
export async function addSalonToFuar(fuarId, salonId) {
  try {
    const res = await axios.post(
      `${FUAR_BASE}/${fuarId}/salon-to-fuar/${salonId}`, {}, { headers: getAuthHeader() }
    );
    return unwrap(res.data);
  } catch (e) { throw e.response ? toError(e, i18n.t("services:fuarAlaniSalonService.salonBuFuaraBaglanamadi")) : e; }
}

/**
 * Backlog #157: bu fuarın fuar alanına kayıtlı, henüz bağlanmamış HER salonu tek seferde bağlar —
 * fuar alanında 13 salon kayıtlıyken bir fuarın Yükseklik Limitleri sekmesinde hiç salon
 * görünmemesi gibi durumlar için (canlıda görüldü, 2026-09-23). Zaten bağlı salonlara dokunmaz.
 * Dönüş: getFuarHMax ile aynı şekil (appliedCount de yok — {message, halls, ...} zarfın `data`sı).
 * POST /fuar/{fuarId}/salon-to-fuar/activate-all
 */
export async function activateAllFairgroundSalons(fuarId) {
  try {
    const res = await axios.post(
      `${FUAR_BASE}/${fuarId}/salon-to-fuar/activate-all`, {}, { headers: getAuthHeader() }
    );
    return unwrap(res.data);
  } catch (e) { throw e.response ? toError(e, i18n.t("services:fuarAlaniSalonService.salonlarBuFuaraBaglanamadi")) : e; }
}

/**
 * Backlog #157: bir salonu BU FUARDAN çıkarır (deaktif eder) -- salon_to_fuar satırı ve onunla
 * birlikte bu fuara özel yükseklik ayarları silinir; salon fuar alanından çıkmaz, yalnızca bu
 * fuardan bağı kesilir. 409 -- bu salonda bu fuar için hâlâ stand var, önce onları taşıyın/kaldırın.
 * DELETE /fuar/{fuarId}/salon-to-fuar/{salonId}
 */
export async function removeSalonFromFuar(fuarId, salonId) {
  try {
    const res = await axios.delete(
      `${FUAR_BASE}/${fuarId}/salon-to-fuar/${salonId}`, { headers: getAuthHeader() }
    );
    return unwrap(res.data);
  } catch (e) { throw e.response ? toError(e, i18n.t("services:fuarAlaniSalonService.salonBuFuardanCikarilamadi")) : e; }
}
